import { nodeHash } from './hash';
import { largestPowerOfTwoBelow, trailingZeros } from './math';

/**
 * Tile geometry per c2sp.org/tlog-tiles with height 8 (256 hashes per tile), plus the RFC 6962
 * PATH / SUBPROOF constructions expressed as leaf ranges so a tile-backed source can serve them.
 *
 * Node addressing: node (level, index) is the root of the perfect subtree over leaves
 * [index * 2^level, (index + 1) * 2^level). Level 0 nodes are leaf hashes.
 */
export const TILE_HEIGHT = 8;
export const TILE_WIDTH = 2 ** TILE_HEIGHT;

export interface TileCoord {
  /** Tile level L; the tile stores nodes at tree level L*8. */
  level: number;
  index: number;
  /** Number of hashes the tile holds for the current tree size (1..256); 256 means full. */
  width: number;
}

/** Encodes a tile index as the path segments `x001/x234/067`. */
export function tileIndexPath(n: number): string {
  if (!Number.isSafeInteger(n) || n < 0) throw new RangeError(`bad tile index ${n}`);
  let s = String(n);
  while (s.length % 3) s = '0' + s;
  const groups = s.match(/.{3}/g)!;
  return groups.map((g, i) => (i < groups.length - 1 ? 'x' + g : g)).join('/');
}

/** Relative URL of a hash tile (`tile/<L>/<N>[.p/<W>]`) or data tile (`tile/data/<N>[.p/<W>]`). */
export function tilePath(level: number | 'data', index: number, width: number = TILE_WIDTH): string {
  const p = `tile/${level}/${tileIndexPath(index)}`;
  if (width < 1 || width > TILE_WIDTH) throw new RangeError(`bad tile width ${width}`);
  return width === TILE_WIDTH ? p : `${p}.p/${width}`;
}

/** How many hashes tile (level, index) holds in a tree of `treeSize` leaves; 0 if it does not exist. */
export function tileWidth(level: number, index: number, treeSize: number): number {
  const nodesAtLevel = Math.floor(treeSize / 2 ** (TILE_HEIGHT * level));
  return Math.max(0, Math.min(TILE_WIDTH, nodesAtLevel - index * TILE_WIDTH));
}

/** Where node (level, index) is found: the tile, offset of its first bottom hash, and how many bottom hashes it spans. */
export function locateNode(level: number, index: number, treeSize: number): { tile: TileCoord; offset: number; span: number } {
  const tileLevel = Math.floor(level / TILE_HEIGHT);
  const r = level - tileLevel * TILE_HEIGHT;
  const span = 2 ** r;
  const first = index * span; // index of the first tree-level (tileLevel*8) node under this node
  const tileIndex = Math.floor(first / TILE_WIDTH);
  return {
    tile: { level: tileLevel, index: tileIndex, width: tileWidth(tileLevel, tileIndex, treeSize) },
    offset: first % TILE_WIDTH,
    span,
  };
}

/** Folds `count` consecutive 32-byte hashes starting at `offset` in a tile into their subtree root. */
export async function hashTileRange(tile: Uint8Array, offset: number, count: number): Promise<Uint8Array> {
  if ((offset + count) * 32 > tile.length) throw new RangeError('tile does not hold the requested hashes');
  let level: Uint8Array[] = [];
  for (let i = 0; i < count; i++) level.push(tile.subarray((offset + i) * 32, (offset + i + 1) * 32));
  while (level.length > 1) {
    const next: Uint8Array[] = [];
    for (let i = 0; i < level.length; i += 2) next.push(await nodeHash(level[i]!, level[i + 1]!));
    level = next;
  }
  return level[0]!;
}

/** Abstract provider of perfect-subtree hashes (backed by tiles, or an in-memory tree in tests). */
export interface NodeSource {
  node(level: number, index: number): Promise<Uint8Array>;
}

export interface Range {
  lo: number;
  hi: number;
}

/** Greedy decomposition of [lo, hi) into maximal aligned perfect subtrees, left to right. */
export function perfectSubtrees(lo: number, hi: number): Array<{ level: number; index: number }> {
  const out: Array<{ level: number; index: number }> = [];
  while (lo < hi) {
    let level = lo === 0 ? Math.floor(Math.log2(hi - lo)) : Math.min(trailingZeros(lo), Math.floor(Math.log2(hi - lo)));
    while (lo + 2 ** level > hi) level--;
    out.push({ level, index: lo / 2 ** level });
    lo += 2 ** level;
  }
  return out;
}

/** MTH(D[lo:hi]) — the RFC 6962 split rule is equivalent to folding the decomposition right to left. */
export async function rangeHash(lo: number, hi: number, src: NodeSource): Promise<Uint8Array> {
  if (hi <= lo) throw new RangeError('empty range');
  const parts = perfectSubtrees(lo, hi);
  const hashes = await Promise.all(parts.map((p) => src.node(p.level, p.index)));
  let acc = hashes[hashes.length - 1]!;
  for (let i = hashes.length - 2; i >= 0; i--) acc = await nodeHash(hashes[i]!, acc);
  return acc;
}

/** RFC 6962 §2.1.1 PATH(m, D[n]) as sibling ranges, leaf-to-root order. */
export function inclusionPathRanges(index: number, treeSize: number): Range[] {
  if (index < 0 || index >= treeSize) throw new RangeError('index out of range');
  const out: Range[] = [];
  let m = index;
  let lo = 0;
  let hi = treeSize;
  // Iterative form of the recursion: descend, recording the sibling at each level; then reverse.
  const pending: Range[] = [];
  while (hi - lo > 1) {
    const k = largestPowerOfTwoBelow(hi - lo);
    if (m < k) {
      pending.push({ lo: lo + k, hi });
      hi = lo + k;
    } else {
      pending.push({ lo, hi: lo + k });
      m -= k;
      lo += k;
    }
  }
  for (let i = pending.length - 1; i >= 0; i--) out.push(pending[i]!);
  return out;
}

/** RFC 6962 §2.1.2 PROOF(m, D[n]) = SUBPROOF(m, D[n], true) as node ranges, in proof order. */
export function consistencyPathRanges(first: number, second: number): Range[] {
  if (first < 0 || first > second) throw new RangeError('bad sizes');
  if (first === second || first === 0) return [];
  const pending: Range[] = [];
  let m = first;
  let lo = 0;
  let hi = second;
  let complete = true;
  while (m < hi - lo) {
    const k = largestPowerOfTwoBelow(hi - lo);
    if (m <= k) {
      pending.push({ lo: lo + k, hi });
      hi = lo + k;
    } else {
      pending.push({ lo, hi: lo + k });
      m -= k;
      lo += k;
      complete = false;
    }
  }
  const out: Range[] = [];
  if (!complete) out.push({ lo, hi }); // SUBPROOF(m, D[m], false) = { MTH(D[m]) }
  for (let i = pending.length - 1; i >= 0; i--) out.push(pending[i]!);
  return out;
}

export async function buildInclusionProof(index: number, treeSize: number, src: NodeSource): Promise<Uint8Array[]> {
  return Promise.all(inclusionPathRanges(index, treeSize).map((r) => rangeHash(r.lo, r.hi, src)));
}

export async function buildConsistencyProof(first: number, second: number, src: NodeSource): Promise<Uint8Array[]> {
  return Promise.all(consistencyPathRanges(first, second).map((r) => rangeHash(r.lo, r.hi, src)));
}

/** Every tile needed to serve the given leaf ranges (deduplicated), for prefetching. */
export function tilesForRanges(ranges: Range[], treeSize: number): TileCoord[] {
  const seen = new Map<string, TileCoord>();
  for (const r of ranges) {
    for (const p of perfectSubtrees(r.lo, r.hi)) {
      const { tile } = locateNode(p.level, p.index, treeSize);
      seen.set(`${tile.level}/${tile.index}`, tile);
    }
  }
  return [...seen.values()];
}
